import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** English mirror of the Thai power-of-attorney-for-use-abroad guide. */
const TITLE = "Thai Power of Attorney for Use Abroad — How to Draft It Without Rejection";
const DESC =
  "How to structure a Thai power of attorney for use abroad: the scope to state, the certification order, and the drafting errors that force a redraft.";
const PATH = "/blog/power-of-attorney-abroad-guide";
const URL = `/en${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "What must a power of attorney for use abroad contain?",
    answer:
      "The identity of the principal and the attorney, the specific acts authorised, any limits on those acts, the period of validity, whether sub-delegation is allowed, the signature of the principal, and the signature of witnesses where the receiving authority expects them. Vague authority is the most common reason a document is rejected.",
  },
  {
    question: "Should the scope be broad or narrow?",
    answer:
      "State each act you actually need in specific terms, and add the routine follow-on acts — requesting records, collecting them, submitting for certification, signing receipts — so the attorney is not stopped mid-process. Sweeping general authority worries receiving officers; a precise list of specific acts does not.",
  },
  {
    question: "Does the document need to be notarised?",
    answer:
      "A power of attorney is a private document, so most destinations require a notarial services attorney to certify the signature before any further certification. That notarisation is what allows the Department of Consular Affairs and the destination embassy to act on it.",
  },
  {
    question: "What order do the certification steps follow?",
    answer:
      "Draft and check the wording, sign before a notarial services attorney, certify with the Department of Consular Affairs where required, translate if the destination requires another language, and finally complete the embassy legalisation or apostille step.",
  },
  {
    question: "Can one document cover several tasks in different offices?",
    answer:
      "Yes, and normally it should. Separate documents for each office multiply certification cost and time. List every task in one instrument, grouping them by office so the officer can see immediately that the act in front of them is covered.",
  },
  {
    question: "How long should the validity period be?",
    answer:
      "Long enough to absorb delays in certification and appointment scheduling, but not open-ended. A period tied to the completion of the stated tasks, with an outer date, is easier for a receiving officer to accept than authority with no end date.",
  },
  {
    question: "What are the most common drafting mistakes?",
    answer:
      "Authority that stops before collection or certification, names transliterated differently from the passport, missing document reference numbers, no provision for sub-delegation when the attorney must use an agent, an incomplete translation that omits the seal text, and certification performed out of order.",
  },
];

export const Route = createFileRoute("/en/blog/power-of-attorney-abroad-guide")({
  component: EnPowerOfAttorneyGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "en" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "en") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "thai power of attorney abroad, notarised power of attorney thailand, power of attorney translation certification, poa legalisation thailand, notary public power of attorney bangkok",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "en"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: "/en" },
              { name: "Blog", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const STRUCTURE: Array<{ part: string; note: string }> = [
  {
    part: "Parties",
    note: "Full names exactly as they appear in the passport, with identity or passport numbers and addresses.",
  },
  {
    part: "Authorised acts",
    note: "Each act stated specifically, including the routine follow-on acts of collection and submission.",
  },
  {
    part: "Documents and references",
    note: "Reference numbers of the records, accounts, or files the attorney will deal with, where known.",
  },
  {
    part: "Limits and sub-delegation",
    note: "State whether the attorney may appoint an agent; silence is often read as a prohibition.",
  },
  {
    part: "Validity period",
    note: "Tie it to completion of the tasks with an outer date rather than leaving it open-ended.",
  },
  {
    part: "Signatures and witnesses",
    note: "Signed by the principal before a notarial services attorney, with witnesses where required.",
  },
];

const SEQUENCE: string[] = [
  "Confirm what the receiving office abroad requires and in which language",
  "Draft the authority, listing each act and its follow-on steps",
  "Check every name and reference against the passport and official records",
  "Sign before a notarial services attorney who certifies the signature",
  "Certify with the Department of Consular Affairs where the destination requires it",
  "Translate in full and complete the embassy legalisation or apostille step",
];

const MISTAKES: string[] = [
  "Authority that ends at 'request the document' and does not cover collection",
  "Names transliterated differently from the passport spelling",
  "No provision for sub-delegation when the attorney must instruct an agent",
  "Open-ended validity with no completion or outer date",
  "Translation that omits seal, stamp, and endorsement text",
  "Certification steps completed out of order, so the chain has to restart",
];

function EnPowerOfAttorneyGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/en" className="text-muted-foreground hover:text-foreground">
          Home
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Blog
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="en" />
      <p className="answer-summary speakable-summary">
        A power of attorney intended for use abroad is rejected for one of two reasons: the authority
        it grants is too narrow to finish the task, or the certification chain was completed in the
        wrong order. Draft the scope around the whole process rather than a single act, then notarise,
        certify, translate, and legalise in that sequence.
      </p>

      <h2>1. What the document should contain</h2>
      <ul>
        {STRUCTURE.map((s) => (
          <li key={s.part}>
            <strong>{s.part}:</strong> {s.note}
          </li>
        ))}
      </ul>

      <h2>2. Scope: specific, but complete</h2>
      <p>
        Receiving officers act only on authority they can read on the page. If the document authorises
        the attorney to request a record but says nothing about collecting it, paying a fee, or
        submitting it for certification, the process stops at the counter. Write the list of acts as a
        sequence — request, collect, pay, submit, receive, sign for — for each office involved.
      </p>

      <h2>3. The certification sequence</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>4. One document, several tasks</h2>
      <p>
        Because every certification step has its own cost and lead time, splitting authority across
        several documents multiplies both. Where the tasks are known in advance, group them by office
        in a single instrument so the officer can identify the relevant clause quickly and the whole
        set travels through certification once.
      </p>

      <h2>5. Drafting errors that force a redraft</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>6. Frequently asked questions</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. References and related services</h2>
      <p className="text-sm">
        References:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          Lawyers Council of Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/en/notary" className="text-primary underline">
            Notarial services attorney
          </Link>
        </li>
        <li>
          <Link to="/en/corporate" className="text-primary underline">
            Corporate document service
          </Link>
        </li>
        <li>
          <Link to="/en/blog/apostille-vs-legalization-by-country" className="text-primary underline">
            Apostille vs legalisation, country by country
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Content reviewed on {PUBLISHED}. This guide is general information for planning purposes and
        is not legal advice on a specific matter. Please confirm current requirements with the
        receiving authority before you proceed.
      </p>
    </article>
  );
}
